import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../editor/nodes/PlaygroundNodes';
import { processMathEquation } from './utils/latexParser';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bot, X, BookOpen } from 'lucide-react';
import { fetchMathConcepts as fetchMathConceptsUtil, clearMathConceptsCache } from '../utils/mathConceptsUtils';
import { Modal } from './common/Modal';
import { MathceptDetailModal, useMathceptDetail } from './common';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { DIFFICULTY_COLORS } from '../pages/LearningStatus/utils/constants';
// AI 실행(LLM 비용)을 쓸 수 있는 계정 유형 — 판정은 이 상수 한 곳에서만 온다
import { AI_RUN_ROLES } from '../utils/roles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const ProblemPreview = ({ 
  isOpen, 
  onClose, 
  problemIds = [], 
  showNumbers = false,
  correctList = [],
  problemNumbers = [],
  selectionMode = false,
  onSelectProblem = null
}) => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getAccessToken, userType } = useAuth();
  // [새 문제 AI 생성]은 v2 [변형] 탭으로 간다 — 변형은 LLM 비용이 나고, 강사에게는 그 탭이 없다(ProblemHub 의 visibleSideTabs).
  // 그래서 강사에게는 버튼을 그리지 않는다(v2 카드 ProblemCardsV2 의 [AI 변형]과 같은 판정).
  const canRunAi = AI_RUN_ROLES.includes(userType);
  const [mathConcepts, setMathConcepts] = useState([]);

  const getDifficultyColor = (difficulty) => {
    return DIFFICULTY_COLORS[difficulty - 1] || 'rgba(156, 163, 175, 0.7)';
  };
  const navigate = useNavigate();
  
  // 매스컨셉 상세 정보 모달 (공통 컴포넌트 사용)
  const { isModalOpen: mathceptDetailModalOpen, loading: mathceptDetailLoading, data: mathceptDetailData, fetchAndShow: fetchMathceptDetails, closeModal: closeMathceptDetailModal } = useMathceptDetail({ handleAuth: false });

  const fetchWithTokenRefresh = useCallback(async (url) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const response = await fetch(`${API_BASE_URL}${url}`, {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('API 요청 실패');
      }

      return await response.json();
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  }, [getAccessToken]);

  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes]
    });

    const processNode = (node) => {
      if (node.type === 'equation') {
        node.equation = processMathEquation(node.equation);
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };

    lexicalData.root = processNode(lexicalData.root);
    const editorState = editor.parseEditorState(lexicalData);

    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });

    return content;
  }, []);

  // Math Concepts 로드
  useEffect(() => {
    const loadMathConcepts = async () => {
      try {
        const concepts = await fetchMathConceptsUtil();
        setMathConcepts(concepts);
      } catch (error) {
        console.error('ProblemPreview: Error loading math concepts:', error);
      }
    };
    
    if (isOpen) {
      loadMathConcepts();
    }
  }, [isOpen]);

  // LaTeX 수식 렌더링 함수
  const renderLatex = (text) => {
    if (!text) return '';
    try {
      // $...$ 패턴을 찾아서 LaTeX로 렌더링
      return text.replace(/\$([^$]+)\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, {
            throwOnError: false,
            displayMode: false
          });
        } catch (e) {
          return match; // 렌더링 실패 시 원본 반환
        }
      });
    } catch (error) {
      return text; // 전체 처리 실패 시 원본 반환
    }
  };



  // 개념 타입별 색상
  const getConceptTypeColor = (type) => {
    const colors = {
      'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
      'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
      'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
      'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
      'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
      'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
      'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
      'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
    };
    return colors[type] || 'rgba(251, 146, 60, 0.5)';
  };

  useEffect(() => {
    if (!isOpen || !problemIds?.length) {
      if (!isOpen) {
        setProblems([]);
      }
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadProblems = async () => {
      setLoading(true);
      try {
        // Math concepts가 없으면 먼저 로드
        let currentMathConcepts = mathConcepts;
        if (currentMathConcepts.length === 0) {
          try {
            const concepts = await fetchMathConceptsUtil();
            setMathConcepts(concepts);
            currentMathConcepts = concepts; // 로드한 concepts를 바로 사용
          } catch (error) {
            console.error('ProblemPreview: Error loading math concepts in loadProblems:', error);
          }
        }
        
        // 1. 배치로 문제 데이터 로드
        let problemsData = [];
        if (problemIds.length > 0) {
          const problemIdsString = problemIds.join(',');
          const response = await fetchWithTokenRefresh(
            `/api/problems/print/page?problem_ids=${problemIdsString}&page=1&per_page=50`
          );
          
          if (response.status === 'success' && response.data) {
            // 응답 데이터를 올바른 순서로 정렬
            problemsData = problemIds.map((id, index) => {
              const problemData = response.data.find(p => p.problem_id === parseInt(id));
              return {
                ...problemData,
                isCorrect: correctList[index] ?? null,
                number: problemNumbers[index] ?? (index + 1)
              };
            }).filter(p => p.problem_id); // 찾지 못한 문제는 제외
          }
        }

        if (!isMounted) return;

        // 2. concept_ids를 가져오기 위해 mathcepts API 호출
        let conceptDataMap = {};
        try {
          console.log('ProblemPreview: Fetching mathcepts by problem ids...');
          const mathceptsResponse = await fetchWithTokenRefresh(
            `/api/mathcepts/temp/by-prob-ids?problem_ids=${problemIds.join(',')}`
          );
          
          if (mathceptsResponse?.status === 'success' && mathceptsResponse?.data) {
            // problem_id를 키로 하는 맵 생성
            conceptDataMap = mathceptsResponse.data.reduce((acc, item) => {
              acc[item.problem_id] = {
                concept_ids: item.concept_ids || [],
                difficulty: item.difficulty
              };
              return acc;
            }, {});
          }
        } catch (error) {
          console.error('Mathcepts 정보 로드 실패:', error);
          // mathcepts 로드 실패해도 계속 진행
        }

        if (isMounted) {
          // 3. concept_ids를 concept 이름으로 변환
          const processedProblems = problemsData.map((problem) => {
            const conceptData = conceptDataMap[problem.problem_id] || {};
            const conceptIds = conceptData.concept_ids || [];
            
            // concept_ids를 mathcepts 객체로 변환
            const mathcepts = {};
            conceptIds.forEach(conceptId => {
              // currentMathConcepts에서 id 필드로 매칭
              const matchedConcept = currentMathConcepts.find(mc => 
                mc.id === conceptId || 
                String(mc.id) === String(conceptId)
              );
              
              if (matchedConcept) {
                mathcepts[conceptId] = matchedConcept.concept_name || matchedConcept.text;
              }
            });
            
            return {
              problem_id: problem.problem_id,
              content: convertLexicalToHtml(problem.lexical_data.lexical_data),
              number: problem.number,
              isCorrect: problem.isCorrect,
              problemInfo: problem.problem_info,
              mathcepts: mathcepts,
              difficulty: conceptData.difficulty
            };
          });
          
          setProblems(processedProblems);
        }
      } catch (error) {
        if (isMounted && error.message !== 'Component unmounted') {
          console.error('문제 로드 실패:', error);
          alert('문제를 불러오는데 실패했습니다.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProblems();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [isOpen, (problemIds || []).join(','), (correctList || []).join(','), (problemNumbers || []).join(','), fetchWithTokenRefresh, convertLexicalToHtml, mathConcepts.length]); // mathConcepts.length 추가

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelectProblem = (problem) => {
    if (selectionMode && onSelectProblem) {
      onSelectProblem({
        problem_id: problem.problem_id,
        problem_info: problem.problemInfo
      });
      onClose();
    }
  };

  const handleCreateNewProblem = (problemId) => {
    onClose();
    // 문제 v2 의 [변형] 탭으로 간다 — v1 문제생성(/problem-agent) 메뉴를 가렸고(2026-10-21),
    // v2 에서 같은 기능(원본 하나로 새 문제 만들기)의 이름이 「변형」이다.
    // 탭은 주소(?tab=)가 정본이라 route state 를 넘기지 않는다(ProblemHub.js 의 탭 주석).
    navigate(`/problem-hub/problem/${problemId}?tab=variation`);
  };

  if (!isOpen) return null;

  const isMobileView = window.innerWidth < 768;

  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      style={{ 
        zIndex: 9999960,
        paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
        paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px'
      }}
      onClick={handleBackdropClick}
    >
      <div 
        className="flex w-[95vw] flex-col rounded-3xl bg-white shadow-xl md:w-[480px]"
        style={{
          height: 'auto',
          maxHeight: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b p-4">
          <h2 className="text-xl font-bold">
            {selectionMode ? '문제 선택' : '문제 미리보기'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full border border-gray-300 p-1 hover:bg-gray-200"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <div className="size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {problems.map((problem) => {
                // 각 문제의 mathcepts에 대해 mathConcepts에서 매칭
                const matchedConcepts = {};
                
                if (problem.mathcepts && Object.keys(problem.mathcepts).length > 0 && mathConcepts.length > 0) {
                  Object.entries(problem.mathcepts).forEach(([id, conceptName]) => {
                    const matchedConcept = mathConcepts.find(
                      mc => mc.concept_name === conceptName || mc.text === conceptName
                    );
                    
                    if (matchedConcept) {
                      matchedConcepts[id] = {
                        concept_name: conceptName,
                        order: matchedConcept.order,
                        concept_type: matchedConcept.concept_type,
                        math_type: matchedConcept.math_type,
                        tags: matchedConcept.tags
                      };
                    } else {
                      matchedConcepts[id] = {
                        concept_name: conceptName,
                        order: null,
                        concept_type: null,
                        math_type: null,
                        tags: null
                      };
                    }
                  });
                }
                
                return (
                <div key={problem.problem_id} className="problem-container">
                  <div className="mb-1 flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">PID: {problem.problem_id}</span>
                      {problem.difficulty && (
                        <div 
                          className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                          style={{ backgroundColor: getDifficultyColor(problem.difficulty) }}
                        >
                          Lv.{problem.difficulty}
                        </div>
                      )}
                    </div>
                    {!selectionMode && canRunAi && (
                      <button
                        onClick={() => handleCreateNewProblem(problem.problem_id)}
                        className="flex items-center gap-1 rounded-3xl bg-[#4ABC85] px-3 py-1 text-xs text-white hover:bg-[#42a478] ml-auto"
                      >
                        <Bot size={14} />
                        새 문제 AI 생성
                      </button>
                    )}
                    {selectionMode && (
                      <button
                        onClick={() => handleSelectProblem(problem)}
                        className="rounded bg-blue-500 px-3 py-1 text-xs text-white hover:bg-blue-600"
                      >
                        선택
                      </button>
                    )}
                  </div>
                  
                  {/* 수학 개념 뱃지 */}
                  {Object.keys(matchedConcepts).length > 0 && (
                    <div className="mb-3 rounded-lg bg-gray-50 p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <BookOpen className="w-4 h-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-600">수학 개념</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(matchedConcepts).map(([id, matched]) => (
                          <div 
                            key={id} 
                            className="flex items-center gap-1 rounded-3xl border border-gray-200 px-2 py-0.5 text-xs text-gray-700 cursor-pointer hover:bg-white transition-colors"
                            onClick={() => fetchMathceptDetails(id)}
                            title="클릭하여 상세 정보 보기"
                          >
                            {matched?.order && (
                              <span 
                                className="inline-flex items-center justify-center font-semibold text-gray-700"
                                style={{
                                  backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                  borderRadius: '6px',
                                  width: '24px',
                                  height: '16px',
                                  minWidth: '24px',
                                  fontSize: '9px'
                                }}
                              >
                                {matched.order}
                              </span>
                            )}
                            
                            {matched?.concept_type && (
                              <span 
                                className="inline-flex items-center justify-center font-semibold text-gray-800"
                                style={{
                                  backgroundColor: getConceptTypeColor(matched.concept_type),
                                  borderRadius: '6px',
                                  width: '24px',
                                  height: '16px',
                                  minWidth: '24px',
                                  fontSize: '8px'
                                }}
                              >
                                {matched.concept_type}
                              </span>
                            )}
                            
                            <span dangerouslySetInnerHTML={{ __html: renderLatex(matched.concept_name) }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="problem-content-wrapper">
                    {showNumbers && (
                      <div 
                        className="problem-number text-2xl font-bold"
                        style={{ 
                          color: problem.isCorrect === null ? '#000000' :
                                 problem.isCorrect ? '#4ABC85' : '#F9344B',
                          minWidth: '36px',
                          marginRight: '20px'
                        }}
                      >
                        {String(problem.number).padStart(2, '0')}
                      </div>
                    )}
                    <div 
                      className="problem-content"
                      style={{ width: showNumbers ? '422px' : '422px' }}
                      dangerouslySetInnerHTML={{ __html: problem.content }}
                    />
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 매스컨셉 상세 정보 모달 - 공통 컴포넌트 사용 (닫기 버튼 없음) */}
      <MathceptDetailModal
        isOpen={mathceptDetailModalOpen}
        onClose={closeMathceptDetailModal}
        data={mathceptDetailData}
        loading={mathceptDetailLoading}
        renderLatex={renderLatex}
        showCloseButton={false}
        useLoader={false}
        handleAuth={false}
      />

      <style>{`
        .problem-content *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 500 !important;
          font-size: 18px !important;
        }
        .katex {
          font-size: 1em !important;
        }
        .PlaygroundEditorTheme__boxContent {
          border: 1px solid black;
          padding: 10px;
          margin: 10px auto;
          max-width: 100%;
          border-radius: 4px;
          background-color: #ffffff;
        }
        .problem-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .problem-content-wrapper {
          display: flex;
          align-items: flex-start;
          width: 100%;
        }
        .problem-content {
          overflow-wrap: break-word;
          word-break: break-word;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ProblemPreview;
